import React, { useCallback, useEffect, useMemo, useState } from "react";

import { OpportunityFormModal } from "../components/OpportunityFormModal";
import { api } from "../lib/api";
import { formatCurrency } from "../lib/format";
import {
  Channel,
  Company,
  Opportunity,
  Pipeline,
  Product,
  UserMini,
} from "../types/crm";

export function OpportunitiesListPage() {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [users, setUsers] = useState<UserMini[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalState, setModalState] = useState<{ open: boolean; opportunity?: Opportunity }>({
    open: false,
  });

  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [productFilter, setProductFilter] = useState<number | "">("");
  const [ownerFilter, setOwnerFilter] = useState<number | "">("");
  const [page, setPage] = useState(1);
  const [csvFeedback, setCsvFeedback] = useState(false);
  const PAGE_SIZE = 25;

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [
        opportunitiesResponse,
        companiesResponse,
        channelsResponse,
        productsResponse,
        pipelinesResponse,
        usersResponse,
      ] = await Promise.all([
        api.get<Opportunity[]>("/opportunities/"),
        api.get<Company[]>("/companies/"),
        api.get<Channel[]>("/channels/"),
        api.get<Product[]>("/products/"),
        api.get<Pipeline[]>("/pipelines/"),
        api.get<UserMini[]>("/users/"),
      ]);
      setOpportunities(opportunitiesResponse.data);
      setCompanies(companiesResponse.data);
      setChannels(channelsResponse.data);
      setProducts(productsResponse.data);
      setPipelines(pipelinesResponse.data);
      setUsers(usersResponse.data.filter((user) => user.is_active));
    } catch {
      setError("Não foi possível carregar as oportunidades.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSaved = (opportunity: Opportunity) => {
    setOpportunities((current) => {
      const exists = current.some((opp) => opp.id === opportunity.id);
      return exists
        ? current.map((opp) => (opp.id === opportunity.id ? opportunity : opp))
        : [opportunity, ...current];
    });
    if (!companies.some((company) => company.id === opportunity.company)) {
      setCompanies((current) => [...current, opportunity.company_detail]);
    }
    setModalState({ open: false });
  };

  const handleDelete = async (opportunity: Opportunity) => {
    if (!window.confirm(`Excluir a oportunidade de ${opportunity.company_detail.name}?`)) return;
    try {
      await api.delete(`/opportunities/${opportunity.id}/`);
      setOpportunities((current) => current.filter((opp) => opp.id !== opportunity.id));
    } catch {
      window.alert("Não foi possível excluir a oportunidade.");
    }
  };

  const stageOptions = useMemo(() => {
    const names = new Set(opportunities.map((o) => o.stage_detail.name));
    return Array.from(names).sort();
  }, [opportunities]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return opportunities.filter((o) => {
      if (q) {
        const hay = `${o.company_detail.name} ${o.primary_contact_detail?.name ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (stageFilter && o.stage_detail.name !== stageFilter) return false;
      if (productFilter !== "" && !o.products.includes(productFilter)) return false;
      if (ownerFilter !== "" && o.owner !== ownerFilter) return false;
      return true;
    });
  }, [opportunities, search, stageFilter, productFilter, ownerFilter]);

  // Abertas primeiro, depois fechadas (spec §9).
  const sorted = useMemo(() => {
    const open = filtered.filter((o) => o.status === "aberta");
    const closed = filtered.filter((o) => o.status !== "aberta");
    return [...open, ...closed];
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const hasFilters = !!search || !!stageFilter || productFilter !== "" || ownerFilter !== "";
  const clearFilters = () => {
    setSearch("");
    setStageFilter("");
    setProductFilter("");
    setOwnerFilter("");
    setPage(1);
  };

  const counts = {
    total: opportunities.length,
    abertas: opportunities.filter((o) => o.status === "aberta").length,
    ganhas: opportunities.filter((o) => o.status === "ganha").length,
    perdidas: opportunities.filter((o) => o.status === "perdida").length,
  };

  const statusLabel = (o: Opportunity) =>
    o.status === "ganha" ? "Ganha" : o.status === "perdida" ? "Perdida" : "Aberta";

  const exportCsv = () => {
    const header = [
      "Empresa", "Produto", "Etapa", "Pipeline", "Status", "MRR",
      "Não recorrente", "Canal", "Responsável",
    ];
    const esc = (v: string) => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const lines = [header.join(";")];
    for (const o of sorted) {
      lines.push(
        [
          o.company_detail.name,
          o.products_detail.map((p) => p.name).join(" | "),
          o.stage_detail.name,
          o.pipeline_detail?.name ?? "",
          statusLabel(o),
          String(o.mrr_estimated),
          String(o.non_recurring_estimated),
          o.channel_detail?.name ?? "",
          o.owner_detail.username,
        ].map(esc).join(";")
      );
    }
    const csv = "﻿" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `oportunidades-minehr-${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setCsvFeedback(true);
    setTimeout(() => setCsvFeedback(false), 2000);
  };

  return (
    <div className="p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Oportunidades</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {counts.total} oportunidades · {counts.abertas} abertas · {counts.ganhas} ganhas ·{" "}
            {counts.perdidas} perdidas
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportCsv}
            className="h-9 rounded-[10px] border border-border-subtle px-3 text-sm font-medium text-slate-600 hover:bg-surface"
          >
            {csvFeedback ? "CSV baixado ✓" : "Exportar CSV"}
          </button>
          <button
            type="button"
            onClick={() => setModalState({ open: true, opportunity: undefined })}
            className="h-9 rounded-[10px] bg-primary px-4 text-sm font-medium text-white hover:bg-primary-dark"
          >
            Nova oportunidade
          </button>
        </div>
      </div>

      {!isLoading && !error && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar empresa ou contato"
            className="h-9 w-56 rounded-[10px] border border-border-subtle px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <select
            aria-label="Etapa"
            value={stageFilter}
            onChange={(e) => {
              setStageFilter(e.target.value);
              setPage(1);
            }}
            className={`h-9 rounded-[10px] border border-border-subtle bg-white px-3 text-sm ${stageFilter ? "text-slate-800" : "text-slate-500"}`}
          >
            <option value="">Todas as etapas</option>
            {stageOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <select
            aria-label="Produto"
            value={productFilter}
            onChange={(e) => {
              setProductFilter(e.target.value ? Number(e.target.value) : "");
              setPage(1);
            }}
            className={`h-9 rounded-[10px] border border-border-subtle bg-white px-3 text-sm ${productFilter !== "" ? "text-slate-800" : "text-slate-500"}`}
          >
            <option value="">Todos os produtos</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Responsável"
            value={ownerFilter}
            onChange={(e) => {
              setOwnerFilter(e.target.value ? Number(e.target.value) : "");
              setPage(1);
            }}
            className={`h-9 w-[198px] rounded-[10px] border border-border-subtle bg-white px-3 text-sm ${ownerFilter !== "" ? "text-slate-800" : "text-slate-500"}`}
          >
            <option value="">Todos os responsáveis</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.username}
              </option>
            ))}
          </select>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-sm font-medium text-primary hover:underline">
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {isLoading && <p className="mt-6 text-sm text-slate-500">Carregando...</p>}

      {!isLoading && error && (
        <div className="mt-6 rounded-[10px] border border-border-subtle bg-surface p-4">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={fetchAll}
            className="mt-3 rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {!isLoading && !error && (
        <div className="mt-6 overflow-x-auto rounded-[10px] border border-border-subtle">
          <table className="min-w-full divide-y divide-border-subtle text-sm">
            <thead className="bg-surface">
              <tr>
                {[
                  "Empresa",
                  "Contato",
                  "Responsável",
                  "Canal",
                  "Etapa",
                  "Produtos",
                  "MRR",
                  "Não recorrente",
                  "Resultado",
                  "",
                ].map((header) => (
                  <th key={header} className="px-4 py-2 text-left font-medium text-slate-600">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center">
                    <p className="text-sm font-medium text-slate-700">Nenhuma oportunidade encontrada</p>
                    <p className="mt-1 text-xs text-slate-500">Tente ajustar a busca ou os filtros.</p>
                  </td>
                </tr>
              )}
              {pageRows.map((opportunity, idx) => {
                const showSeparator =
                  idx > 0 &&
                  pageRows[idx - 1].status === "aberta" &&
                  opportunity.status !== "aberta";
                const closed = opportunity.status !== "aberta";
                return (
                  <React.Fragment key={opportunity.id}>
                    {showSeparator && (
                      <tr>
                        <td colSpan={10} className="bg-surface px-4 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                          Fechadas
                        </td>
                      </tr>
                    )}
                    <tr
                      className={`cursor-pointer hover:bg-surface ${closed ? "opacity-70" : ""}`}
                      onClick={() => setModalState({ open: true, opportunity })}
                    >
                      <td className="px-4 py-2 text-slate-800">{opportunity.company_detail.name}</td>
                      <td className="px-4 py-2 text-slate-600">
                        {opportunity.primary_contact_detail?.name ?? "—"}
                      </td>
                      <td className="px-4 py-2 text-slate-600">{opportunity.owner_detail.username}</td>
                      <td className="px-4 py-2 text-slate-600">{opportunity.channel_detail?.name ?? "—"}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-slate-600">{opportunity.stage_detail.name}</td>
                      <td className="px-4 py-2 text-slate-600">
                        {opportunity.products_detail.map((p) => p.name).join(", ")}
                      </td>
                      <td className="px-4 py-2 text-slate-600">{formatCurrency(opportunity.mrr_estimated)}</td>
                      <td className="px-4 py-2 text-slate-600">
                        {formatCurrency(opportunity.non_recurring_estimated)}
                      </td>
                      <td className="px-4 py-2">
                        {opportunity.result === "ganho" && (
                          <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent-dark">
                            Ganho
                          </span>
                        )}
                        {opportunity.result === "perdido" && (
                          <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">
                            Perdido
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => setModalState({ open: true, opportunity })}
                          className="mr-3 text-sm font-medium text-primary hover:underline"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(opportunity)}
                          className="text-sm font-medium text-red-600 hover:underline"
                        >
                          Excluir
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && !error && sorted.length > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
          <span>
            Mostrando {(currentPage - 1) * PAGE_SIZE + 1}–
            {Math.min(currentPage * PAGE_SIZE, sorted.length)} de {sorted.length} oportunidades
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
              className="rounded-[8px] border border-border-subtle px-2 py-1 disabled:opacity-40"
            >
              ←
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`rounded-[8px] px-2.5 py-1 ${
                  p === currentPage ? "bg-primary text-white" : "border border-border-subtle text-slate-600"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(currentPage + 1)}
              className="rounded-[8px] border border-border-subtle px-2 py-1 disabled:opacity-40"
            >
              →
            </button>
          </div>
        </div>
      )}

      {modalState.open && (
        <OpportunityFormModal
          opportunity={modalState.opportunity}
          companies={companies}
          channels={channels}
          products={products}
          pipelines={pipelines}
          users={users}
          onClose={() => setModalState({ open: false })}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
